import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, Plus, X, Send, Menu } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { useParams, useSearchParams  } from 'react-router-dom';

const ChatBot = () => {
  const [chats, setChats] = useState([]);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [message, setMessage] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  
  const [ job_id ] = useSearchParams();
  
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const API_URL = process.env.NODE_ENV === 'development' ? "https://demo.aiguardian.io/agent-backend": "/agent-backend";
  // Initialize chats on component mount
  useEffect(() => {
    const savedChats = JSON.parse(localStorage.getItem('chatbot_chats') || '[]');
    if (savedChats.length === 0) {
      createNewChat();
    } else {
      setChats(savedChats);
      setCurrentChatId(savedChats[0].id);
    }
  }, []);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [chats, currentChatId]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  }, [message]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const saveChats = (updatedChats) => {
    localStorage.setItem('chatbot_chats', JSON.stringify(updatedChats));
  };

  const createNewChat = () => {
    const chatId = 'chat_' + Date.now();
    const newChat = {
      id: chatId,
      title: 'New Chat',
      messages: [{
        content: "Hello! How can I assist you today?",
        sender: 'bot',
        timestamp: new Date().toISOString()
      }],
      createdAt: new Date().toISOString()
    };
    
    const updatedChats = [newChat, ...chats];
    setChats(updatedChats);
    setCurrentChatId(chatId);
    setIsSidebarOpen(false);
  };

  const loadChat = (chatId) => {
    setCurrentChatId(chatId);
    setIsSidebarOpen(false); // Close sidebar on mobile after selection
  };

  const deleteChat = (chatIdToDelete) => {
    const updatedChats = chats.filter(chat => chat.id !== chatIdToDelete);
    setChats(updatedChats);
    saveChats(updatedChats);

    // If the deleted chat was the current one, switch to another chat or create a new one
    if (currentChatId === chatIdToDelete) {
      if (updatedChats.length > 0) {
        setCurrentChatId(updatedChats[0].id);
      } else {
        createNewChat();
      }
    }
  };

  const callChatbotAPI = async (messages) => {
    // Transform messages to the format expected by the backend (e.g., OpenAI's format)
    const formattedMessages = messages.map(({ sender, content }) => ({
      role: sender === 'user' ? 'user' : 'assistant',
      content: content,
    }));

    const response = await fetch(API_URL + '/api/chatbot', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      // Send the entire history
      body: JSON.stringify({ msg: formattedMessages,job_id: job_id.get('id'),sys_id: job_id.get('userid')}),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'An unknown API error occurred.' }));
      throw new Error(errorData.detail || `API error: ${response.statusText}`);
    }

    const data = await response.json();
    return data.reply; // Assumes the backend response is a JSON with a "reply" key
  };

  const sendMessage = async () => {
    const messageText = message.trim();
    if (!messageText || isTyping) return;

    // Add user message
    const currentChat = chats.find(chat => chat.id === currentChatId);
    if (!currentChat) return;

    const userMessage = {
      content: messageText,
      sender: 'user',
      timestamp: new Date().toISOString()
    };

    const updatedMessages = [...currentChat.messages, userMessage];
    
    // Update chat title if it's the first user message
    let updatedTitle = currentChat.title;
    if (currentChat.messages.filter(m => m.sender === 'user').length === 0) {
      updatedTitle = messageText.length > 30 ? messageText.substring(0, 30) + '...' : messageText;
    }

    const updatedChats = chats.map(chat => 
      chat.id === currentChatId 
        ? { ...chat, messages: updatedMessages, title: updatedTitle }
        : chat
    );

    setChats(updatedChats);
    setMessage('');
    setIsTyping(true);
    saveChats(updatedChats);

    try {
      // Call the API with the full message history including the new user message
      const botResponse = await callChatbotAPI(updatedMessages);
      
      const botMessage = {
        content: botResponse,
        sender: 'bot',
        timestamp: new Date().toISOString()
      };

      const finalUpdatedChats = chats.map(chat => 
        chat.id === currentChatId 
          ? { ...chat, messages: [...updatedMessages, botMessage], title: updatedTitle }
          : chat
      );

      setChats(finalUpdatedChats);
      saveChats(finalUpdatedChats);
    } catch (error) {
      const errorMessage = {
        content: `Sorry, I encountered an error: ${error.message}`,
        sender: 'bot',
        timestamp: new Date().toISOString()
      };

      const finalUpdatedChats = chats.map(chat => 
        chat.id === currentChatId 
          ? { ...chat, messages: [...updatedMessages, errorMessage], title: updatedTitle }
          : chat
      );

      setChats(finalUpdatedChats);
      saveChats(finalUpdatedChats);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const currentChat = chats.find(chat => chat.id === currentChatId);
  const currentMessages = currentChat?.messages || [];

  return (
    <div className="flex h-screen bg-gray-50 text-gray-800">
      {/* Sidebar */}
      <div className={`w-64 bg-white border-r border-gray-200 flex flex-col transition-transform duration-300 shadow-lg
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 fixed md:relative z-50 h-full`}>
        
        {/* Sidebar Header */}
        <div className="p-4 border-b border-gray-200">
          <button 
            onClick={createNewChat}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium"
          >
            <Plus size={18} />
            New Chat
          </button>
        </div>

        {/* Chat History */}
        <div className="flex-1 overflow-y-auto p-2">
          {chats.map(chat => (
            <div
              key={chat.id}
              className={`flex items-center justify-between p-3 mb-1 rounded-lg cursor-pointer transition-colors group
                ${chat.id === currentChatId ? 'bg-emerald-50 border border-emerald-200' : 'hover:bg-gray-100'}`}
              onClick={() => loadChat(chat.id)}
            >
              <div className="flex-1 truncate text-sm">
                {chat.title}
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  deleteChat(chat.id);
                }}
                className="opacity-0 group-hover:opacity-100 ml-2 p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-all"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Overlay for mobile */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 p-4 border-b border-gray-200 bg-white">
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="p-2 hover:bg-gray-100 rounded-lg md:hidden"
          >
            <Menu size={20} />
          </button>
          <MessageSquare className="text-emerald-600" size={24} />
          <h1 className="text-xl font-semibold">PEAR ChatBot</h1>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4">
          {currentMessages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Welcome to ChatBot</h2>
              <p className="text-gray-600">Start a conversation by typing a message below</p>
            </div>
          ) : (
            <div className="max-w-5xl mx-auto space-y-6">
              {currentMessages.map((msg, index) => (
                <div key={index} className={`flex gap-3 ${msg.sender === 'user' ? 'flex-row-reverse' : ''}`}>
                  {/* <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white font-semibold text-sm
                    ${msg.sender === 'user' ? 'bg-emerald-600' : 'bg-indigo-600'}`}>
                    {msg.sender === 'user' ? 'U' : 'B'}
                  </div> */}
                  <div className={`flex-1 max-w-3xl p-4 rounded-xl border
                    ${msg.sender === 'user' 
                      ? 'bg-emerald-50 border-emerald-200 text-gray-800' 
                      : 'bg-white border-gray-200 text-gray-800'}`}>
                    <ReactMarkdown>{msg.content}</ReactMarkdown>
                  </div>
                </div>
              ))}
              
              {/* Typing indicator */}
              {isTyping && (
                <div className="flex gap-3">
                  {/* <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-indigo-600 text-white font-semibold text-sm">
                    
                  </div> */}
                  <div className="flex-1 max-w-3xl p-4 rounded-xl border bg-white border-gray-200">
                    <div className="flex items-center gap-1">
                      <div className="flex gap-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input Area */}
        <div className="border-t border-gray-200 bg-white p-4">
          <div className="max-w-5xl mx-auto relative">
            <textarea
              ref={textareaRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message here..."
              className="w-full p-4 pr-12 border border-gray-300 rounded-xl resize-none outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 min-h-[52px] max-h-[120px]"
              disabled={isTyping}
              rows={1}
            />
            <button
              onClick={sendMessage}
              disabled={!message.trim() || isTyping}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 p-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatBot;